// src/components/layout/index.tsx
import React from "react";
import { motion } from "motion/react";
import {
  stackLayout,
  rowLayout,
  pageSection,
  pageHeader,
  actionHeader,
  gridLayout,
  mainLayout,
  backdrop,
  scrollContainer
} from "@pinatagrams/design-system/recipes";
import { DURATION, EASING } from "@pinatagrams/design-system";
import { jsx } from "react/jsx-runtime";
var Stack = React.forwardRef(
  ({ gap = "md", align = "stretch", skeleton = false, className, ...props }, ref) => {
    const { root } = stackLayout({ gap, align, skeleton });
    return /* @__PURE__ */ jsx("div", { ref, className: `${root}${className ? ` ${className}` : ""}`, ...props });
  }
);
Stack.displayName = "Stack";
var Inline = React.forwardRef(
  ({ gap = "md", align = "center", justify = "start", wrap = true, className, ...props }, ref) => {
    const { root } = rowLayout({ gap, align, justify, wrap });
    return /* @__PURE__ */ jsx("div", { ref, className: `${root}${className ? ` ${className}` : ""}`, ...props });
  }
);
Inline.displayName = "Inline";
var PageSection = React.forwardRef(
  ({ maxWidth = "lg", className, ...props }, ref) => {
    const { root } = pageSection({ maxWidth });
    return /* @__PURE__ */ jsx("section", { ref, className: `${root}${className ? ` ${className}` : ""}`, ...props });
  }
);
PageSection.displayName = "PageSection";
var PageHeader = React.forwardRef(
  ({ className, ...props }, ref) => {
    const { root } = pageHeader({});
    return /* @__PURE__ */ jsx("header", { ref, className: `${root}${className ? ` ${className}` : ""}`, ...props });
  }
);
PageHeader.displayName = "PageHeader";
var HeaderRoot = React.forwardRef(
  ({ sticky = false, skeleton = false, className, ...props }, ref) => {
    const { root } = actionHeader({ sticky, skeleton });
    return /* @__PURE__ */ jsx("header", { ref, className: `${root}${className ? ` ${className}` : ""}`, ...props });
  }
);
HeaderRoot.displayName = "Header";
var HeaderTitleSection = React.forwardRef(({ className, ...props }, ref) => {
  const { title } = actionHeader({});
  return /* @__PURE__ */ jsx("div", { ref, className: `${title}${className ? ` ${className}` : ""}`, ...props });
});
HeaderTitleSection.displayName = "Header.Title";
var HeaderActionsSection = React.forwardRef(({ className, ...props }, ref) => {
  const { actions } = actionHeader({});
  return /* @__PURE__ */ jsx("div", { ref, className: `${actions}${className ? ` ${className}` : ""}`, ...props });
});
HeaderActionsSection.displayName = "Header.Actions";
var Header = Object.assign(HeaderRoot, {
  Title: HeaderTitleSection,
  Actions: HeaderActionsSection
});
var ActionHeader = Header;
var Main = React.forwardRef(
  ({ gap = "lg", padding = "lg", skeleton = false, className, ...props }, ref) => {
    const { root } = mainLayout({ gap, padding, skeleton });
    return /* @__PURE__ */ jsx("main", { ref, className: `${root}${className ? ` ${className}` : ""}`, ...props });
  }
);
Main.displayName = "Main";
var Grid = React.forwardRef(
  ({
    gap = "md",
    justify = "center",
    align = "start",
    items = "center",
    overflow = "auto",
    flex = "fill",
    padding = "lg",
    className,
    ...props
  }, ref) => {
    const { root } = gridLayout({ gap, justify, align, items, overflow, flex, padding });
    return /* @__PURE__ */ jsx("section", { ref, className: `${root}${className ? ` ${className}` : ""}`, ...props });
  }
);
Grid.displayName = "Grid";
var Overlay = React.forwardRef(
  ({ status, origin, pos, elevation, size, children, exited }, ref) => {
    const coords = origin || pos;
    const maskType = origin ? "origin" : pos !== void 0 ? "pos" : void 0;
    const { root } = backdrop({ status, elevation, size });
    return /* @__PURE__ */ jsx(
      motion.div,
      {
        ref,
        className: root,
        variants: getBackdropVariants(coords, maskType, exited),
        custom: coords,
        initial: "closed",
        animate: "open",
        exit: "closed",
        children
      }
    );
  }
);
Overlay.displayName = "Overlay";
var layout_default = {
  Stack,
  Inline,
  PageSection,
  PageHeader,
  Header,
  Main,
  Grid,
  Overlay
};
var getBackdropVariants = (coords, maskType, exited) => {
  const transitions = {
    backgroundSize: {
      duration: DURATION.slow,
      ease: EASING.out
    },
    backdropFilter: {
      ease: EASING.out,
      duration: DURATION.normal
    },
    opacity: {
      duration: DURATION.normal,
      ease: EASING.inOut
    },
    maskImage: {
      duration: DURATION.fast,
      ease: EASING.out
    }
  };
  const baseVariants = {
    open: {
      backgroundSize: "4px 4px",
      backdropFilter: "brightness(0.7) blur(3px)",
      opacity: 1
    },
    closed: {
      backgroundSize: "3px 3px",
      backdropFilter: "brightness(1) blur(5px)",
      opacity: 0
    }
  };
  if (coords === void 0 || maskType === void 0) {
    return {
      open: { ...baseVariants.open },
      closed: { ...baseVariants.closed }
    };
  }
  if (maskType === "origin") {
    return {
      open: ({ x, y }) => ({
        ...baseVariants.open,
        maskImage: `radial-gradient(circle at ${x}px ${y}px, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 10%, rgba(0,0,0,0) 100%, rgba(0,0,0,0) 100%)`,
        transition: {
          ...transitions,
          maskImage: {
            duration: DURATION.slow,
            ease: EASING.in
          }
        }
      }),
      closed: ({ x, y }) => ({
        ...baseVariants.closed,
        maskImage: `radial-gradient(circle at ${x}px ${y}px, rgba(0,0,0,0) 0%, rgba(0,0,0,0) 100%, rgba(0,0,0,1) 100%, rgba(0,0,0,1) 100%)`,
        transition: {
          maskImage: {
            duration: DURATION.slow,
            ease: EASING.out
          }
        }
      })
    };
  }
  return {
    open: ({ x, y }) => {
      if (exited) {
        return {
          ...baseVariants.open,
          maskImage: `radial-gradient(circle at ${x}px ${y}px, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 100%, rgba(0,0,0,0) 100%, rgba(0,0,0,0) 100%)`,
          transition: {
            ...transitions,
            maskImage: {
              duration: DURATION.slow,
              ease: EASING.in
            }
          }
        };
      }
      return {
        ...baseVariants.open,
        maskImage: `radial-gradient(circle at ${x}px ${y}px, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 40%, rgba(0,0,0,0) 100%, rgba(0,0,0,0) 100%)`,
        transition: {
          ...transitions,
          maskImage: {
            duration: DURATION.instant
          }
        }
      };
    },
    closed: ({ x, y }) => {
      if (exited) {
        return {
          ...baseVariants.closed,
          maskImage: `radial-gradient(circle at ${x}px ${y}px, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 100%, rgba(0,0,0,0) 100%, rgba(0,0,0,0) 100%)`,
          transition: {
            maskImage: {
              duration: DURATION.slow,
              ease: EASING.out
            }
          }
        };
      }
      return {
        ...baseVariants.closed,
        maskImage: `radial-gradient(circle at ${x}px ${y}px, rgba(0,0,0,0) 0%, rgba(0,0,0,0) 100%, rgba(0,0,0,1) 100%, rgba(0,0,0,1) 100%)`,
        transition: {
          maskImage: {
            duration: DURATION.slow,
            ease: EASING.out
          }
        }
      };
    }
  };
};
var ScrollContainer = React.forwardRef(({ className, ...props }, ref) => {
  const { root } = scrollContainer({ size: "element" });
  return /* @__PURE__ */ jsx("div", { ref, className: `${root}${className ? ` ${className}` : ""}`, ...props });
});
ScrollContainer.displayName = "ScrollContainer";
export {
  ActionHeader,
  Grid,
  Header,
  HeaderActionsSection,
  HeaderRoot,
  HeaderTitleSection,
  Inline,
  Main,
  Overlay,
  PageHeader,
  PageSection,
  ScrollContainer,
  Stack,
  layout_default as default
};
//# sourceMappingURL=layout.js.map